// Pure Segment-Mathematik der Chef-Tages-Timeline (Design 1b): Stempel → Arbeits-/Pausen-Segmente in
// Werkstatt-Wandminuten (relativ zu HEUTE; Vortag negativ → Nachtschichten laufen ins Fenster hinein),
// gerendert als SVG-Rects (Prozent-Attribute — CSP-fest, keine Inline-Styles).
import { formatWallClock, localDayKey, type AttendanceReportStamp } from '@wf/shared'

/** Sichtfenster der Timeline wie im Design: 06:00–18:00. */
export const TIMELINE_START_MIN = 6 * 60
export const TIMELINE_END_MIN = 18 * 60

export interface DaySegment {
  startMin: number
  endMin: number
  kind: 'work' | 'pause'
  /** true = läuft noch (bis JETZT gezeichnet, heller gerendert). */
  open: boolean
}

/** Werkstatt-Wandminute (0–1439) eines Zeitpunkts. */
export function wallMinuteOfDay(epochMs: number): number {
  const [h, m] = formatWallClock(epochMs).split(':').map(Number) as [number, number]
  return h * 60 + m
}

/**
 * Wandminute RELATIV zu `todayKey`: Vortags-Stempel werden negativ (−1440…), Folgetags positiv (+1440…).
 * Damit startet eine Nachtschicht (kommen Mo 22:00) ein Segment, das am Di ab Fensterbeginn sichtbar
 * ist, statt komplett zu fehlen (Review-Fund Nachtschicht-Timeline). Mehr als ±1 Tag kommt aus dem
 * 2-Tage-Report nicht vor — wird defensiv trotzdem korrekt gerechnet.
 */
export function minuteRelativeToDay(epochMs: number, todayKey: string): number {
  const key = localDayKey(epochMs)
  const dayDelta = Math.round((Date.parse(key + 'T12:00:00Z') - Date.parse(todayKey + 'T12:00:00Z')) / 86_400_000)
  return wallMinuteOfDay(epochMs) + dayDelta * 1440
}

/** Minute → Prozentposition im 06–18-Fenster, an die Ränder geklemmt. */
export function timelinePct(minute: number): number {
  const clamped = Math.min(TIMELINE_END_MIN, Math.max(TIMELINE_START_MIN, minute))
  return ((clamped - TIMELINE_START_MIN) / (TIMELINE_END_MIN - TIMELINE_START_MIN)) * 100
}

/**
 * Stempel (chronologisch, entwertete werden ignoriert) → Segmente. Ein offener Zustand (eingestempelt/
 * Pause ohne Folgebuchung) läuft bis `nowMin` weiter — wie die „Jetzt"-Linie im Design. `todayKey`
 * verankert die Minuten-Achse: Stempel vom VORTAG (2-Tage-Report) werden negativ und lassen eine
 * Nachtschicht ab Fensterbeginn sichtbar werden; timelinePct klemmt ohnehin auf 06–18.
 */
export function buildDaySegments(stamps: readonly AttendanceReportStamp[], nowMin: number, todayKey?: string): DaySegment[] {
  const valid = stamps.filter((s) => !s.voided).sort((a, b) => a.tsMs - b.tsMs)
  const segs: DaySegment[] = []
  let curStart: number | null = null
  let curKind: 'work' | 'pause' = 'work'
  const push = (endMin: number, open: boolean) => {
    if (curStart != null && endMin > curStart) segs.push({ startMin: curStart, endMin, kind: curKind, open })
  }
  for (const s of valid) {
    const m = todayKey ? minuteRelativeToDay(s.tsMs, todayKey) : wallMinuteOfDay(s.tsMs)
    if (s.kind === 'kommen') {
      curStart = m
      curKind = 'work'
    } else if (s.kind === 'pause_start') {
      push(m, false)
      curStart = m
      curKind = 'pause'
    } else if (s.kind === 'pause_ende') {
      push(m, false)
      curStart = m
      curKind = 'work'
    } else if (s.kind === 'gehen') {
      push(m, false)
      curStart = null
    }
  }
  if (curStart != null) push(Math.max(curStart, nowMin), true)
  return segs
}
